<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue';

// 모바일 감지
const isMobile = ref(false);
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

// 1. Props (템플릿에서 사용하는 모든 외부 데이터)
const props = defineProps({
  // (Props... 기존과 동일 ... )
  inputdata: Object,
  transdata: Object,
  birthData: Object,
  pgmode: String,
  langMode: String,
  colorMode: String,
  sortMode: String,
  toggleProMode: String,
  isSaved: Boolean,
  memoCount: Number,
  userGroupName: String,
  selectedGroup: Object,
  solarDate: String,
  solarWeekday: String,
  lunarDate: String,
  useFun: Object,
  five: Object,
  showHanja: Function,
  showIlganBadge: { type: Boolean, default: false },
  isUndeletable: { type: Boolean, default: false },
});

// 2. Emits (템플릿에서 호출하는 모든 함수)
const emit = defineEmits([
  'openUserName',
  'searchEowayTrans',
  'searchEowayTransColor',
  'sortChange',
  'changeProMode',
  'handleGroupChange',
  'openMemoRegFirst',
  'regSave',
  'delComLast',
  'goList',
  'clickIlganIcon',
]);

// 3. [추가됨] 템플릿 ref="hiddenBtn"을 위한 선언
const hiddenBtn = ref(null);

/** 그룹명은 5글자까지만 표시 */
const truncateText = (text, maxLength) => {
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + '...';
  }
  return text;
};
</script>

<template>
  <!-- ========== 모바일 UI (피그마 기준) ========== -->
  <div v-if="isMobile" class="mobile-result-header">
    <div class="mobile-inner-wrap">
    <!-- 프로필 영역 -->
    <div class="mobile-profile-section">
      <div class="mobile-profile-icon-wrap" @click="showIlganBadge && emit('clickIlganIcon')">
        <div class="mobile-profile-icon" :class="five.getColorFive(birthData.sky.day.value)">
          {{ showHanja(birthData.sky.day.value, 'sky') || birthData.sky.day.value }}
        </div>
        <span v-if="showIlganBadge" class="mobile-profile-search-badge">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="7" cy="7" r="4.5" stroke="#fff" stroke-width="1.5"/>
            <path d="M10.5 10.5L13.5 13.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </span>
      </div>
      <div class="mobile-profile-info">
        <div class="mobile-profile-name">
          <span class="mobile-profile-name-text">{{ inputdata.name || '-' }}</span><span class="mobile-profile-gender">{{ useFun.getGenderTxt(inputdata.gender) === '남' ? '(남)' : '(여)' }}</span>
          <span class="mobile-profile-age">{{ transdata?.kor_age }}세(만{{ transdata?.man_age }}세)</span>
          <button
            v-if="pgmode === 'rs'"
            type="button"
            class="mobile-edit-btn"
            @click="emit('openUserName')"
          >
            수정
          </button>
        </div>
        <div class="mobile-date-info">
          <span class="mobile-date-label orange">양력</span>
          {{ useFun.formatCustomDate(solarDate) }}
          {{ five.getBirthTimeTxt(five.getTimeTxt2(inputdata.hour) + ':' + five.getTimeTxt2(inputdata.min)) }}
          /
          <span class="mobile-date-label blue">음력</span>
          {{ useFun.formatCustomLunar(lunarDate) }}
        </div>
        <div class="mobile-option-info">
          <span v-if="transdata?.bornArea?.county">{{ transdata?.bornArea.county }} (동경 {{ transdata?.bornArea?.offset || '30' }}분), </span>
          {{ useFun.formatOption4Txt(inputdata.koreantime) }}
          {{ useFun.formatOption1Txt(inputdata.earlytime) }}
          {{ useFun.formatOption2Txt(inputdata.dongji) }}
        </div>
        <!-- 버튼 영역 -->
        <div class="mobile-profile-buttons">
          <div class="mobile-profile-buttons__left">
            <button
              type="button"
              class="mobile-profile-btn"
              @click="emit('handleGroupChange', selectedGroup)"
            >
              {{ userGroupName }}
            </button>
            <button type="button" class="mobile-profile-btn" @click="emit('openMemoRegFirst')">
              메모 <span class="memo-count">{{ memoCount }}</span>
            </button>
          </div>
          <button
            v-if="pgmode === 'rs' && !isUndeletable"
            type="button"
            class="mobile-delete-btn"
            @click="emit('delComLast')"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.25 8.25L16.3497 17.3565C16.3228 17.7349 16.1535 18.0891 15.8758 18.3477C15.5982 18.6063 15.2329 18.75 14.8535 18.75H8.6465C8.26646 18.751 7.90022 18.6076 7.62179 18.349C7.34335 18.0903 7.17348 17.7356 7.1465 17.3565L6.5 8.25M9.75 11.25V15.75M13.75 11.25V15.75M14.25 8.25V6C14.25 5.80109 14.171 5.61032 14.0303 5.46967C13.8897 5.32902 13.6989 5.25 13.5 5.25H10C9.80109 5.25 9.61032 5.32902 9.46967 5.46967C9.32902 5.61032 9.25 5.80109 9.25 6V8.25M5.5 8.25H18" stroke="#949494" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>

  <!-- ========== PC UI (기존) ========== -->
  <div v-else class="people-box">
    <!-- identity start -->
    <div class="mem-info">
      <div class="profile-box">
        <div class="profile-img">
          {{ showHanja(birthData.sky.day.value, 'sky') || birthData.sky.day.value }}
        </div>
        <!-- <span class="icon-search-s-red btn-set">검색</span> -->
      </div>
      <div class="name-box-d">
        <div v-if="inputdata.name" class="name-gen">
          <div class="name-d">
            {{ inputdata.name }}
          </div>
        </div>

        <div v-else class="name-d">-</div>
        <div class="age">
          <!-- {{ inputdata.name }} -->
          {{ useFun.getGenderTxt(inputdata.gender) }} {{ transdata?.kor_age }}세 (만{{
            transdata?.man_age
          }}세)
          <!-- {{ calculateAge() }}세 -->
        </div>
      </div>
      <div v-if="pgmode === 'rs'" class="btn-mod-box">
        <button v-if="pgmode === 'rs'" class="btn-modify" @click="emit('openUserName')">
          수정
        </button>
      </div>
    </div>
    <div class="divider"></div>
    <div class="birth-box">
      <div class="mb6">
        <div class="mr32">
          <span class="tits orange">양력</span>
          <span class="txts"
            >{{ useFun.formatCustomDate(solarDate) }} ({{ solarWeekday }})
            {{
              five.getBirthTimeTxt(
                five.getTimeTxt2(inputdata.hour) + ':' + five.getTimeTxt2(inputdata.min),
              )
            }}
          </span>
        </div>
        <div class="">
          <span class="tits blue">음력</span>
          <span class="txts">{{ useFun.formatCustomLunar(lunarDate) }}</span>
        </div>
      </div>
      <div class="w-wide">
        <span class="tits">옵션</span>
        <span class="txts">
          <span v-if="transdata?.bornArea?.county">
            {{ transdata?.bornArea.county }}, {{ transdata?.bornArea.city }},
          </span>
          {{ useFun.formatOption4Txt(inputdata.koreantime) }}
          {{ useFun.formatOption1Txt(inputdata.earlytime) }}
          {{ useFun.formatOption2Txt(inputdata.dongji) }}
        </span>
      </div>
    </div>
    <!-- identity finish -->

    <!-- tools start -->
    <div ref="hiddenBtn" class="btn-box btn-rd trans-btn-box trans-btn-list-box">
      <!-- //TODO : 전문가 버전 체크 -->
      <span class="" v-if="toggleProMode === 'nor'">
        <v-row class="btn-tooltip">
          <button
            v-if="langMode === 'CHNKOR'"
            class="btn-rd btn-rd-38 chnkor"
            @click="emit('searchEowayTrans', 'CHN')"
          >
            한자/한글
          </button>
          <!-- @click="searchEowayTrans('CHNKOR')" -->
        </v-row>
        <v-row class="btn-tooltip">
          <button
            v-if="langMode === 'CHN'"
            class="btn-rd btn-rd-38 chn"
            @click="emit('searchEowayTrans', 'KOR')"
          >
            한자
          </button>
        </v-row>
        <v-row class="btn-tooltip">
          <button
            v-if="langMode === 'KOR'"
            class="btn-rd btn-rd-38 kor"
            @click="emit('searchEowayTrans', 'CHNKOR')"
          >
            한글
          </button>
        </v-row>
        <v-row class="btn-tooltip">
          <button
            v-if="colorMode === 'fntcolor01'"
            class="btn-rd btn-rd-38 fntcolor01"
            @click="emit('searchEowayTransColor', 'fntcolor02')"
          >
            색상변환01
          </button>
          <!-- @click="searchEowayTrans('CHNKOR')" -->
        </v-row>
        <v-row class="btn-tooltip">
          <button
            v-if="colorMode === 'fntcolor02'"
            class="btn-rd btn-rd-38 fntcolor02"
            @click="emit('searchEowayTransColor', 'fntcolor03')"
          >
            색상변환02
          </button>
        </v-row>

        <v-row class="btn-tooltip">
          <button
            v-if="colorMode === 'fntcolor03'"
            class="btn-rd btn-rd-38 fntcolor03"
            @click="emit('searchEowayTransColor', 'fntcolor01')"
          >
            색상변환03
          </button>
        </v-row>
      </span>
      <button
        class="btn-rd btn-rd-38 sorting"
        :class="{ on: sortMode === 'asc' }"
        @click="emit('sortChange')"
      >
        순서변경
      </button>
      <button
        v-if="toggleProMode === 'pro'"
        class="btn-line st-toggle"
        @click="emit('changeProMode', 'nor')"
      >
        <!-- <i class="i-round-100 gray"></i>  -->
        <span class="on">프로</span>
        <span class="off">일반</span>
      </button>
      <!--//FIX 실서버 반영되면 안되어서 주석 처리-->
      <button
        v-if="toggleProMode === 'nor'"
        class="btn-line st-toggle orange"
        @click="emit('changeProMode', 'pro')"
      >
        <span class="off">프로</span>
        <span class="on">일반</span>
        <!-- <i class="i-round-100 orange"></i> -->
      </button>
      <!-- <div class="" v-if="toggleProMode === 'nor'">
                    <button
                      class="btn-rd btn-rd-38 grally"
                      :class="{ on: rsDataNor === true }"
                      @click="handleDefaultReport()"
                    >
                      갤러리형
                    </button>
                  </div> -->
      <!-- <v-btn
                    class="btn-nor"
                    @click="downloadAsImage"
                  >
                    다운로드 (test)
                  </v-btn> -->
      <span class="line" />
      <button class="btn-line txt-ellipsis" @click="emit('handleGroupChange', selectedGroup)">
        <span>{{ truncateText(userGroupName, 5) }}</span>
      </button>
      <button class="btn-line" @click="emit('openMemoRegFirst')">
        <span class="name">메모</span> <span class="count">{{ memoCount }}</span>
      </button>
      <span class="line" />
      <button
        v-if="pgmode !== 'rs'"
        class="btn-save none-icon btn-sq-40 btn"
        :class="{ active: isSaved }"
        @click="emit('regSave')"
      >
        {{ isSaved ? '저장완료' : '저장하기' }}
      </button>
      <button v-if="pgmode === 'rs' && !isUndeletable" class="btn-del-line" @click="emit('delComLast')">삭제</button>
      <button
        v-if="pgmode === 'rs'"
        class="btn-list list btn-sq-40 btn"
        @click="emit('goList', 'options')"
      >
        목록으로
      </button>
    </div>
    <!-- tools finish -->
  </div>
</template>

<style lang="scss" scoped>
// 모바일 사주 결과 헤더 스타일 (피그마 기준)
.mobile-result-header {
  background: #F5F5F5;
  padding: 8px 16px;
  border-top: none;
}

.mobile-profile-section {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 0;
  border-top: none;
}

.mobile-profile-icon-wrap {
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
}

.mobile-profile-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;  // 피그마 기준 원형
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 400;
  font-family: 'ChosunGs';
  color: #404040;
  flex-shrink: 0;
  background: #E5E5E5;

  // 오행 색상 - 피그마 기준
  &.five01 { background: #C23535; color: #fff; } // 화(火) - 빨강
  &.five02 { background: #404040; color: #fff; } // 수(水) - 검정
  &.five03 { background: #5E9F1C; color: #fff; } // 목(木) - 초록
  &.five04 { background: #949494; color: #fff; } // 금(金) - 회색
  &.five05 { background: #DB9823; color: #fff; } // 토(土) - 노랑
}

.mobile-profile-search-badge {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #EB4C10;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.mobile-profile-info {
  flex: 1;
  min-width: 0;
}

.mobile-profile-name {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 20px;
  min-width: 0; // 긴 이름이 형제 요소를 밀어내지 않도록 자식 축소 허용
}

// 이름 텍스트가 길면 한 줄 말줄임 처리 (성별/나이/수정 버튼 레이아웃 보존)
.mobile-profile-name-text {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mobile-profile-detail {
  font-size: 15px;
  font-weight: 500;
  color: #575757;
  line-height: 20px;
}

.mobile-profile-buttons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.mobile-profile-buttons__left {
  display: flex;
  gap: 4px;
  align-items: center;
}

.mobile-profile-btn {
  padding: 6px 12px;
  border-radius: 16px;  // 피그마 기준
  background: #fff;
  border: 1px solid #EBEBEB;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  white-space: nowrap;
  line-height: 18px;

  // 메모 카운트 색상
  .memo-count {
    color: #EB4C10;
  }

  &:active {
    background: #F5F5F5;
  }
}

// 이름 옆 성별
.mobile-profile-gender {
  font-weight: 700;
  color: #333;
  flex-shrink: 0;
}

// 이름 옆 나이
.mobile-profile-age {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-left: 0;
  flex-shrink: 0;
  white-space: nowrap;
}

// 수정 버튼
.mobile-edit-btn {
  margin-left: auto;
  flex-shrink: 0;
  padding: 0;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 700;
  color: #EB4C10;
  cursor: pointer;
}

// 날짜 정보 (피그마 기준 inline)
.mobile-date-info {
  font-size: 14px;
  font-weight: 500;
  color: #757575;
  line-height: 18px;
  margin-top: 4px;

  .mobile-date-label {
    font-weight: 500;

    &.orange {
      color: #FF6E6E;
    }

    &.blue {
      color: #5790FC;
    }
  }
}

// 옵션 정보
.mobile-option-info {
  font-size: 14px;
  font-weight: 500;
  color: #757575;
  line-height: 18px;
  margin-top: 2px;
}

// 삭제 버튼 (아이콘 + 원형 테두리)
.mobile-delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: #fff;
  border: 1px solid #EBEBEB;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;

  svg {
    width: 22px;
    height: 22px;
  }

  &:active {
    background: #F5F5F5;
  }
}
</style>
